import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { background, height, maxWidth, overflow, space } from 'styled-system';

import ContainerWithBackground from 'common/components/ContainerWithBackground';
import { Flex } from 'common/components/Flex';
import RectShape from 'common/components/RectShape';
import { IMAGE_RATIO } from './VisualNavigationItem';

/**
 * There is no need to use the amount of slides loaded from GQL as this data is now loaded on the
 * client side. Therefore, I chose the fixed value of 8 because it is greater than the largest
 * number of slides visible at any breakpoint. So the placeholder will always be loaded as if
 * there were the amount needed to fill the entire slider.
 */
const FIXED_SLIDE_COUNT = 8;
/**
 * These quotients are the result of the division between the whole component width and the
 * width of the slides in the CommonVisualNavigation component.
 * Values: ['small', 'medium', 'large', 'extra-large']
 */
const SLIDE_WIDTH_QUOTIENT = [2.46, 4.16, 4.7, 5.88];

interface PlaceholderProps {
  showTitle?: boolean;
  showSubtitle?: boolean;
  showLoadingAnimation?: boolean;
  forceVisibleSlidesCount?: number;
  withBackground?: boolean;
}

const UnstyledContainerBase = styled(Flex)<any>`
  ${maxWidth};
  ${background};
  ${overflow};
`;

const containerBaseStyles: ComponentProps<typeof UnstyledContainerBase> = {
  width: 1,
  maxWidth: 1600,
  mx: 'auto',
  mb: ['sp_24', null, null, 'sp_32'],
  flexDirection: 'column',
  overflow: 'hidden',
};

const ContainerBase = (props: ComponentProps<typeof UnstyledContainerBase>) => (
  <UnstyledContainerBase {...containerBaseStyles} {...props} />
);

const UnstyledStyledContainerWithBackground = styled(ContainerWithBackground)<any>`
  ${overflow};
`;

const StyledContainerWithBackground = (
  props: ComponentProps<typeof UnstyledStyledContainerWithBackground>
) => (
  <UnstyledStyledContainerWithBackground {...containerBaseStyles} overflow="hidden" {...props} />
);

const UnstyledTitle = styled(RectShape)<any>`
  ${height};
  ${space};
`;

const Title = (props: ComponentProps<typeof UnstyledTitle>) => (
  <UnstyledTitle
    width={['85px', '95px', null, '105px']}
    height={['18px', '20px', null, '22px']}
    {...props}
  />
);

const UnstyledSubtitle = styled(RectShape)<any>`
  ${height};
  ${space};
`;

const Subtitle = (props: ComponentProps<typeof UnstyledSubtitle>) => (
  <UnstyledSubtitle
    width={['185px', '195px', null, '205px']}
    height={['18px', '20px', null, '22px']}
    mt={['sp_8', null, 'sp_12']}
    mb={['sp_12', 'sp_20', 'sp_24']}
    {...props}
  />
);

const SlidesContainer = styled(Flex)``;

const UnstyledSlide = styled(Flex)``;

const Slide = (props: ComponentProps<typeof UnstyledSlide>) => (
  <UnstyledSlide
    pr={['sp_16', '18px', 'sp_20', 'sp_16']}
    alignItems="center"
    flexDirection="column"
    {...props}
  />
);

const UnstyledCategoryImage = styled(RectShape)<any>`
  ${height};
  ${space};
`;

const CategoryImage = (props: ComponentProps<typeof UnstyledCategoryImage>) => (
  <UnstyledCategoryImage pb={`calc(100% * ${IMAGE_RATIO})`} {...props} />
);

const UnstyledCategoryName = styled(RectShape)<any>`
  ${height};
  ${space};
`;

const CategoryName = (props: ComponentProps<typeof UnstyledCategoryName>) => (
  <UnstyledCategoryName height={[9, 22, null, 24]} mt="sp_2" {...props} />
);

const getContainerWidth = (slidesCount?: number) => {
  if (slidesCount) {
    return '100%';
  }

  return SLIDE_WIDTH_QUOTIENT.map(quotient => `calc((100% / ${quotient}) * ${FIXED_SLIDE_COUNT})`);
};

const getSliceWidth = (slidesCount?: number) => {
  if (slidesCount) {
    return `calc(100% / ${slidesCount})`;
  }

  return SLIDE_WIDTH_QUOTIENT.map(quotient => `calc(100% / ${quotient})`);
};

const Placeholder = ({
  showTitle = false,
  showSubtitle = false,
  showLoadingAnimation,
  forceVisibleSlidesCount,
  withBackground,
}: PlaceholderProps) => {
  const Container = withBackground ? StyledContainerWithBackground : ContainerBase;

  return (
    <Container
      data-testid="custom-placeholder-container"
      className={showLoadingAnimation && 'show-loading-animation'}
    >
      {showTitle && <Title mb={showSubtitle ? 'sp_0' : ['sp_12', 'sp_20', 'sp_24']} />}
      {showSubtitle && <Subtitle />}
      <SlidesContainer width={getContainerWidth(forceVisibleSlidesCount)}>
        {[...Array(forceVisibleSlidesCount || FIXED_SLIDE_COUNT)].map((_, index) => (
          <Slide key={index} width={getSliceWidth(forceVisibleSlidesCount)}>
            <CategoryImage />
            <CategoryName />
          </Slide>
        ))}
      </SlidesContainer>
    </Container>
  );
};

export default Placeholder;
